import { AspectRatio, Box, Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import {
  IconExternalLink,
  IconFileTypePdf,
  IconHeadphones,
  IconVideo,
} from '@tabler/icons-react';
import type { TrainingBlockView } from '@creativitas/shared';
import { AssetImage } from '@/components/ui/AssetImage';

/**
 * Renders one material block (§45, §46).
 *
 * Media is never proxied or downloaded: an embeddable provider is shown inline,
 * anything else becomes a clear external-link action.
 */

/** Assets served by this prototype, safe to render inline. */
const isLocalAsset = (url: string | null): boolean =>
  Boolean(url?.startsWith('/assets/') || url?.startsWith('/uploads/'));

function BlockShell({ title, children }: { title: string | null; children: React.ReactNode }) {
  return (
    <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
      <Stack gap="sm">
        {title ? <Title order={4}>{title}</Title> : null}
        {children}
      </Stack>
    </Paper>
  );
}

function ExternalAction({
  url,
  label,
  icon,
}: {
  url: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <Group gap="sm">
      <Button
        component="a"
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        variant="light"
        leftSection={icon}
        rightSection={<IconExternalLink size={14} />}
      >
        {label}
      </Button>
      <Text size="xs" c="var(--text-subtle)" style={{ wordBreak: 'break-all' }}>
        {url}
      </Text>
    </Group>
  );
}

export function TrainingBlock({ block }: { block: TrainingBlockView }) {
  switch (block.type) {
    case 'text':
      return (
        <BlockShell title={block.title}>
          {/* Plain paragraphs — deliberately not a rich editor (§45). */}
          <Stack gap="sm">
            {(block.body ?? '').split(/\n{2,}/).map((paragraph, index) => (
              <Text key={index} style={{ whiteSpace: 'pre-line' }}>
                {paragraph}
              </Text>
            ))}
          </Stack>
          {/*
            Текст может нести иллюстрацию — сказка сектора это текст вместе со
            своей сценой. Пропорции у картинки свои: рамка карточной формы
            обрезала бы всё, что на карту не похоже.
          */}
          {isLocalAsset(block.url) ? (
            <Box maw={360}>
              <AssetImage src={block.url} alt={block.title ?? 'Иллюстрация'} ratio="auto" />
            </Box>
          ) : null}
        </BlockShell>
      );

    /*
      Картинка сама по себе (§бэклог 11). Прежде она была довеском к тексту — и
      значит, её нельзя было поставить ни без текста, ни двумя подряд, ни над
      текстом. Показывается в своих пропорциях: форму ей никто не назначал.
    */
    case 'image':
      return (
        <BlockShell title={block.title}>
          {isLocalAsset(block.url) ? (
            <Stack gap={6}>
              <Box maw={560}>
                <AssetImage src={block.url} alt={block.title ?? 'Изображение'} ratio="auto" />
              </Box>
              {block.body ? (
                <Text size="sm" c="var(--text-muted)">
                  {block.body}
                </Text>
              ) : null}
            </Stack>
          ) : (
            <Text size="sm" c="var(--text-muted)">
              Картинка не выбрана.
            </Text>
          )}
        </BlockShell>
      );

    case 'video':
      return (
        <BlockShell title={block.title}>
          {block.embedUrl ? (
            <AspectRatio ratio={16 / 9}>
              <iframe
                src={block.embedUrl}
                title={block.title ?? 'Видео'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
                style={{ border: 0, borderRadius: 'var(--radius-card)' }}
              />
            </AspectRatio>
          ) : block.url ? (
            <ExternalAction url={block.url} label="Открыть видео" icon={<IconVideo size={16} />} />
          ) : (
            <Text size="sm" c="var(--text-muted)">
              Ссылка на видео не указана.
            </Text>
          )}
        </BlockShell>
      );

    case 'audio':
      return (
        <BlockShell title={block.title}>
          {block.url ? (
            <ExternalAction
              url={block.url}
              label="Открыть аудио"
              icon={<IconHeadphones size={16} />}
            />
          ) : (
            <Text size="sm" c="var(--text-muted)">
              Ссылка на аудио не указана.
            </Text>
          )}
        </BlockShell>
      );

    case 'pdf':
      return (
        <BlockShell title={block.title}>
          {block.url ? (
            <ExternalAction
              url={block.url}
              label="Открыть PDF"
              icon={<IconFileTypePdf size={16} />}
            />
          ) : (
            <Text size="sm" c="var(--text-muted)">
              Ссылка на файл не указана.
            </Text>
          )}
        </BlockShell>
      );

    case 'external_link': {
      // Local images (sector tales) are shown inline; anything
      // else opens in a new tab.
      const isLocalImage = isLocalAsset(block.url);

      return (
        <BlockShell title={block.title}>
          {block.body ? <Text>{block.body}</Text> : null}
          {isLocalImage && block.url ? (
            <AssetImage src={block.url} alt={block.title ?? 'Иллюстрация'} ratio="7 / 12" />
          ) : block.url ? (
            <ExternalAction
              url={block.url}
              label="Перейти по ссылке"
              icon={<IconExternalLink size={16} />}
            />
          ) : (
            <Text size="sm" c="var(--text-muted)">
              Ссылка не указана.
            </Text>
          )}
        </BlockShell>
      );
    }
  }
}
